import { getDateFromNric, getGenderFromNric } from '@/utils/applicant';
import { formatRequestDate, safeDate } from '@/utils/date';
import { getPreferredLang } from '@/utils/preferredLang';
import { useProductCode } from '@/utils/product';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  usePostApplicationMutation,
  usePostQuickQuotationMutation,
} from '../apis';
import {
  Answer,
  ClientType,
  Gender,
  IdType,
  PaymentMode,
  Relationship,
} from '../enums';
import { useAppDispatch, useAppSelector } from '../reducers';
import {
  updateDeductible,
  updateIsEdit,
  updatePaymentMode,
  updatePlanCode,
  updatePlans,
  updatePremiums,
  updateRefId,
} from '../reducers/application';
import { planSchema } from '../schemas';
import { PlanFormValues, QuotationFormValues } from '../types/form';
import { Applicant } from '../types/model';
import { useApplication } from './useApplication';
import { useProductType, useRoute } from './useRoute';
import { AddToCartEvent, EcommerceItem } from '../types/googleAnalytics/planGA';
import {
  CURRENCY,
  ENTITY_CODE_INSURANCE,
  ENTITY_CODE_TAKAFUL,
  INSURANCE,
  ProductLabel,
} from '../constants';
import { string } from 'prop-types';
import { useOptions } from './useOptions';
import { useGAFactory } from './useGAFactory';
import { pushDataLayer } from '@/utils/googleAnalytics';

export const usePlan = () => {
  const dispatch = useAppDispatch();
  const application = useAppSelector((state) => state.application);
  const { redirect } = useRoute();
  const { i18n } = useTranslation();
  const [postQuickQuote, { isLoading: isLoadingPost }] =
    usePostQuickQuotationMutation();
  const [postApplication, { isLoading: isLoadingUpdate }] =
    usePostApplicationMutation();
  const productCode = useProductCode();
  const { getApplicationRequestData } = useApplication();
  const productType = useProductType();
  const productLabel = ProductLabel[productType];
  const routeSection = useRoute();
  const { getProtections } = useOptions();
  const GAFactory = useGAFactory();
  useEffect(() => {
    if (!application.formValues?.quotation) {
      redirect('quotation');
    }
  }, [application.formValues?.quotation]);

  const isEdit = application.isEdit === true;
  const plans = application.plans;

  const formMethods = useForm<PlanFormValues>({
    resolver: yupResolver(planSchema),
    defaultValues: {
      deductible: application.deductible,
      paymentMode: application.paymentMode,
      planCode: application.planCode,
    },
  });

  const selectedPlanCode = formMethods.watch('planCode');
  const paymentMode = formMethods.watch('paymentMode');
  const deductible = formMethods.watch('deductible');

  const getRelationship = (formValues: QuotationFormValues): Relationship => {
    // get policy owner relationship
    if (
      formValues.life_insured.gender === Gender.MALE &&
      formValues.life_insured.relationship === Relationship.Spouse
    ) {
      return Relationship.Husband;
    } else if (
      formValues.life_insured.gender === Gender.FEMALE &&
      formValues.life_insured.relationship === Relationship.Spouse
    ) {
      return Relationship.Wife;
    } else {
      return Relationship.Self;
    }
  };

  const getApplicantsInfo = (
    formValues: QuotationFormValues,
    planCode?: string,
  ): Applicant[] => {
    const lifeInsured = formValues.life_insured;
    const policyOwner = formValues.policy_owner;
    const result: Applicant[] = [];
    if (lifeInsured.relationship === Relationship.Self) {
      result.push({
        clientType: ClientType.ProposerOrLifeAssured,
        planCode,
        gender: formValues.life_insured.gender as Gender,
        name: {
          fullName: formValues.life_insured.name as string,
        },
        emails: [
          {
            type: 'email',
            value: formValues.life_insured.email as string,
            isPrimary: Answer.Yes,
          },
        ],
        idType: IdType.IC,
        idNo: formValues.life_insured.nric as string,
        relationship: formValues.life_insured.relationship as Relationship,
        dob: formatRequestDate(
          getDateFromNric(formValues.life_insured.nric as string),
        ),
        occupationCode: formValues.life_insured.occupation,
        deductibleAmount: deductible,
      } as Applicant);
    } else {
      if (policyOwner) {
        result.push({
          clientType: ClientType.Proposer,
          planCode,
          gender: formValues.life_insured?.gender as Gender,
          name: {
            fullName: formValues.policy_owner?.name as string,
          },
          emails: [
            {
              type: 'email',
              value: formValues.policy_owner?.email as string,
              isPrimary: Answer.Yes,
            },
          ],
          idType: IdType.IC,
          idNo: formValues.policy_owner?.nric as string,
          relationship:
            lifeInsured.relationship !== Relationship.Spouse
              ? policyOwner.gender === Gender.MALE
                ? Relationship.Father
                : Relationship.Mother
              : Relationship.Spouse,
          dob: formatRequestDate(
            safeDate(getDateFromNric(formValues.policy_owner?.nric as string)),
          ),
          occupationCode: formValues.policy_owner?.occupation,
          deductibleAmount: deductible,
        } as Applicant);
      }
      if (lifeInsured) {
        {
          result.push({
            clientType: ClientType.LifeAssured,
            planCode,
            gender: getGenderFromNric(lifeInsured.nric ?? ''),
            name: {
              fullName: formValues.life_insured.name as string,
            },
            emails: [
              {
                type: 'email',
                value: formValues.life_insured.email as string,
                isPrimary: Answer.Yes,
              },
            ],
            idType: IdType.IC,
            idNo: formValues.life_insured.nric as string,
            relationship: Relationship.Self,
            dob: formatRequestDate(
              getDateFromNric(formValues.life_insured.nric as string),
            ),
            occupationCode: formValues.life_insured.occupation,
            deductibleAmount: deductible,
          } as Applicant);
        }
      }
    }

    return result.map((applicant, index) => ({
      ...applicant,
      id: index + 1,
    }));
  };

  const submitQuotation = async (planCode?: string) => {
    const formValues = application.formValues.quotation!;
    const result = await postQuickQuote({
      appId: application.appId,
      distributionChannel: application.distributionChannel,
      paymentMode: paymentMode,
      preferredLang: getPreferredLang(i18n.language),
      productCode,
      applicants: getApplicantsInfo(formValues, planCode),
    }).unwrap();

    if (!planCode) {
      dispatch(updatePlans(result.data.planPremium));
    }

    dispatch(
      updatePremiums({
        monthlyTotalPremium: result.data.monthlyTotalPremium,
        yearlyTotalPremium: result.data.yearlyTotalPremium,
        annualTotalPremium: result.data.annualTotalPremium,
      }),
    );

    return result;
  };

  const submitUpdateApplication = async (
    paymentMode: PaymentMode,
    planCode?: string,
  ) => {
    const data = getApplicationRequestData(application.formValues.application!);
    data.applicants = data.applicants.map((applicant) => ({
      ...applicant,
      planCode,
    }));
    data.paymentMode = paymentMode;
    const result = await postApplication({ body: data }).unwrap();
    return result;
  };

  useEffect(() => {
    if (deductible !== application.deductible) {
      dispatch(updateDeductible(deductible));
      submitQuotation();
    }
  }, [deductible]);

  const selectedPlan = useMemo(
    () => plans.find(({ planCode }) => planCode === selectedPlanCode),
    [selectedPlanCode, plans],
  );

  const onUpdate = async (formValues: PlanFormValues) => {
    dispatch(updatePaymentMode(formValues.paymentMode));
    dispatch(updatePlanCode(formValues.planCode));

    try {
      const result = await submitUpdateApplication(
        formValues.paymentMode,
        formValues.planCode,
      );

      dispatch(
        updatePremiums({
          monthlyTotalPremium: result.data.monthlyTotalPremium,
          yearlyTotalPremium: result.data.yearlyTotalPremium,
          annualTotalPremium: result.data.annualTotalPremium,
        }),
      );

      dispatch(updateRefId(result.refId));
      dispatch(updateIsEdit(false));
      redirect('review');
    } catch (error) {
      redirect('error');
    }
  };

  const onSubmit = async (formValues: PlanFormValues) => {
    dispatch(updatePaymentMode(formValues.paymentMode));
    dispatch(updatePlanCode(formValues.planCode));
    pushDataLayer(
      GAFactory('add_to_cart', {
        planCode: formValues.planCode,
        paymentModeFormValue: formValues.paymentMode,
        deductible: application.deductible,
      }),
    );
    try {
      const result = await submitQuotation(formValues.planCode);
      dispatch(updateRefId(result.refId));
      redirect('questionnaire');
    } catch (error) {
      redirect('error');
    }
  };

  const onBack = () => {
    redirect('quotation');
  };

  return {
    plans,
    selectedPlan,
    paymentMode,
    formMethods,
    isEdit,
    isLoading: isLoadingPost || isLoadingUpdate,
    onUpdate,
    onSubmit,
    onBack,
  };
};
